import React, { type FunctionComponent } from 'react'
import { css } from '@emotion/react'
import { useParams } from 'react-router'
import { spacing } from '@retailer-platform/shared-components'
import { PageHeader } from '@retailer-platform/shared-components'
import { useGoToPath } from 'utils/routing/navigation.hooks'
import { BannerBreadcrumbs } from 'domains/banners/components/banner-breadcrumbs/BannerBreadcrumbs'
import DocumentTitle from 'legacy/components/DocumentTitle'
import { type VisibilityConditionQueryParams } from 'domains/banners/types/visibilityCondition.types'
import { VisibilityConditionForm } from 'domains/banners/components/visibility-condition-create/VisibilityConditionForm'
import { type VisibilityConditionFormData } from 'domains/banners/types/visibilityCondition.types'
import { VisibilityConditionTypeEnum } from 'domains/banners/constants/visibilityCondition.constants'
import {
  useCreateCountryIdsVisibilityCondition,
  useCreateDayOfWeekVisibilityCondition,
  useCreateExpressMembershipVisibilityCondition,
  useCreateExpressNonmemberLongTermHoldoutVisibilityCondition,
  useCreateMatchesAnyRetailerIdVisibilityCondition,
  useCreateMinimumPlatformVersionVisibilityCondition,
  useCreateRetailerAvailabilityVisibilityCondition,
  useCreateShoppedAtRetailerVisibilityCondition,
  useCreateShoppedVisibilityCondition,
  useCreateUserInteractionLimitVisibilityCondition,
  useCreateWhitelistedPostalCodesVisibilityCondition,
  useCreateWhitelistedZonesVisibilityCondition,
} from 'domains/banners/api/createVisibilityCondition.hooks'
import {
  mapFormDataToCountryIdsInput,
  mapFormDataToDayOfWeekInput,
  mapFormDataToExpressMembershipInput,
  mapFormDataToExpressNonmemberLongTermHoldoutInput,
  mapFormDataToMatchesAnyRetailerIdInput,
  mapFormDataToMinimumPlatformVersionInput,
  mapFormDataToRetailerAvailabilityInput,
  mapFormDataToShoppedAtRetailerInput,
  mapFormDataToShoppedInput,
  mapFormDataToUserInteractionLimitInput,
  mapFormDataToWhitelistedPostalCodesInput,
  mapFormDataToWhitelistedZonesInput,
} from 'domains/banners/utils/visibilityConditionUtils'
import { useQueryBanner } from 'domains/banners/utils/useQueryBanner.hooks'

const cardsContainer = css({
  margin: '0 auto',
  paddingTop: spacing.X32,
  paddingRight: spacing.X20,
  paddingBottom: spacing.X32,
  paddingLeft: spacing.X20,
  width: '80%',
})

export const VisibilityConditionCreate: FunctionComponent = () => {
  const { placementId } = useParams<VisibilityConditionQueryParams>()

  // TODO: Go to newly created details page when BannerDetails is ready
  const goToList = useGoToPath('app-admin-banners-visibility-conditions', {
    placementId: placementId,
  })

  const { apiSuccess: apiSuccessDetails, apiResult: apiResultDetails } = useQueryBanner({
    placementId: placementId,
  })

  // Graph Mutations
  const {
    createCountryIdsVisibilityCondition,
    apiLoading: apiLoadingCountryIdsVisibilityCondition,
    apiError: apiErrorCountryIdsVisibilityCondition,
  } = useCreateCountryIdsVisibilityCondition()

  const {
    createDayOfWeekVisibilityCondition,
    apiLoading: apiLoadingDayOfWeekVisibilityCondition,
    apiError: apiErrorDayOfWeekVisibilityCondition,
  } = useCreateDayOfWeekVisibilityCondition()

  const {
    createExpressMembershipVisibilityCondition,
    apiLoading: apiLoadingExpressMembershipVisibilityCondition,
    apiError: apiErrorExpressMembershipVisibilityCondition,
  } = useCreateExpressMembershipVisibilityCondition()

  const {
    createExpressNonmemberLongTermHoldoutVisibilityCondition,
    apiLoading: apiLoadingExpressNonmemberLongTermHoldoutVisibilityCondition,
    apiError: apiErrorExpressNonmemberLongTermHoldoutVisibilityCondition,
  } = useCreateExpressNonmemberLongTermHoldoutVisibilityCondition()

  const {
    createMatchesAnyRetailerIdVisibilityCondition,
    apiLoading: apiLoadingMatchesAnyRetailerIdVisibilityCondition,
    apiError: apiErrorMatchesAnyRetailerIdVisibilityCondition,
  } = useCreateMatchesAnyRetailerIdVisibilityCondition()

  const {
    createMinimumPlatformVersionVisibilityCondition,
    apiLoading: apiLoadingMinimumPlatformVersionVisibilityCondition,
    apiError: apiErrorMinimumPlatformVersionVisibilityCondition,
  } = useCreateMinimumPlatformVersionVisibilityCondition()

  const {
    createRetailerAvailabilityVisibilityCondition,
    apiLoading: apiLoadingRetailerAvailabilityVisibilityCondition,
    apiError: apiErrorRetailerAvailabilityVisibilityCondition,
  } = useCreateRetailerAvailabilityVisibilityCondition()

  const {
    createShoppedAtRetailerVisibilityCondition,
    apiLoading: apiLoadingShoppedAtRetailerVisibilityCondition,
    apiError: apiErrorShoppedAtRetailerVisibilityCondition,
  } = useCreateShoppedAtRetailerVisibilityCondition()

  const {
    createShoppedVisibilityCondition,
    apiLoading: apiLoadingShoppedVisibilityCondition,
    apiError: apiErrorShoppedVisibilityCondition,
  } = useCreateShoppedVisibilityCondition()

  const {
    createUserInteractionLimitVisibilityCondition,
    apiLoading: apiLoadingUserInteractionLimitVisibilityCondition,
    apiError: apiErrorUserInteractionLimitVisibilityCondition,
  } = useCreateUserInteractionLimitVisibilityCondition()

  const {
    createWhitelistedPostalCodesVisibilityCondition,
    apiLoading: apiLoadingWhitelistedPostalCodesVisibilityCondition,
    apiError: apiErrorWhitelistedPostalCodesVisibilityCondition,
  } = useCreateWhitelistedPostalCodesVisibilityCondition()

  const {
    createWhitelistedZonesVisibilityCondition,
    apiLoading: apiLoadingWhitelistedZonesVisibilityCondition,
    apiError: apiErrorWhitelistedZonesVisibilityCondition,
  } = useCreateWhitelistedZonesVisibilityCondition()

  // Submit Handlers
  const handleCountryIdsVisibilityConditionFormSubmit = (
    placementId: string,
    formData: VisibilityConditionFormData
  ) =>
    createCountryIdsVisibilityCondition({
      visibilityConditionData: mapFormDataToCountryIdsInput(placementId, formData),
    })

  const handleDayOfWeekVisibilityConditionFormSubmit = (
    placementId: string,
    formData: VisibilityConditionFormData
  ) =>
    createDayOfWeekVisibilityCondition({
      visibilityConditionData: mapFormDataToDayOfWeekInput(placementId, formData),
    })

  const handleExpressMembershipVisibilityConditionFormSubmit = (
    placementId: string,
    formData: VisibilityConditionFormData
  ) =>
    createExpressMembershipVisibilityCondition({
      visibilityConditionData: mapFormDataToExpressMembershipInput(placementId, formData),
    })

  const handleExpressNonmemberLongTermHoldoutVisibilityConditionFormSubmit = (
    placementId: string,
    formData: VisibilityConditionFormData
  ) =>
    createExpressNonmemberLongTermHoldoutVisibilityCondition({
      visibilityConditionData: mapFormDataToExpressNonmemberLongTermHoldoutInput(
        placementId,
        formData
      ),
    })

  const handleMatchesAnyRetailerIdVisibilityConditionFormSubmit = (
    placementId: string,
    formData: VisibilityConditionFormData
  ) =>
    createMatchesAnyRetailerIdVisibilityCondition({
      visibilityConditionData: mapFormDataToMatchesAnyRetailerIdInput(placementId, formData),
    })

  const handleMinimumPlatformVersionVisibilityConditionFormSubmit = (
    placementId: string,
    formData: VisibilityConditionFormData
  ) =>
    createMinimumPlatformVersionVisibilityCondition({
      visibilityConditionData: mapFormDataToMinimumPlatformVersionInput(placementId, formData),
    })

  const handleRetailerAvailabilityVisibilityConditionFormSubmit = (
    placementId: string,
    formData: VisibilityConditionFormData
  ) =>
    createRetailerAvailabilityVisibilityCondition({
      visibilityConditionData: mapFormDataToRetailerAvailabilityInput(placementId, formData),
    })

  const handleShoppedAtRetailerVisibilityConditionFormSubmit = (
    placementId: string,
    formData: VisibilityConditionFormData
  ) =>
    createShoppedAtRetailerVisibilityCondition({
      visibilityConditionData: mapFormDataToShoppedAtRetailerInput(placementId, formData),
    })

  const handleShoppedVisibilityConditionFormSubmit = (
    placementId: string,
    formData: VisibilityConditionFormData
  ) =>
    createShoppedVisibilityCondition({
      visibilityConditionData: mapFormDataToShoppedInput(placementId, formData),
    })

  const handleUserInteractionLimitVisibilityConditionFormSubmit = (
    placementId: string,
    formData: VisibilityConditionFormData
  ) =>
    createUserInteractionLimitVisibilityCondition({
      visibilityConditionData: mapFormDataToUserInteractionLimitInput(placementId, formData),
    })

  const handleWhitelistedPostalCodesVisibilityConditionFormSubmit = (
    placementId: string,
    formData: VisibilityConditionFormData
  ) =>
    createWhitelistedPostalCodesVisibilityCondition({
      visibilityConditionData: mapFormDataToWhitelistedPostalCodesInput(placementId, formData),
    })

  const handleWhitelistedZonesVisibilityConditionFormSubmit = (
    placementId: string,
    formData: VisibilityConditionFormData
  ) =>
    createWhitelistedZonesVisibilityCondition({
      visibilityConditionData: mapFormDataToWhitelistedZonesInput(placementId, formData),
    })

  const isSubmitting =
    apiLoadingCountryIdsVisibilityCondition ||
    apiLoadingDayOfWeekVisibilityCondition ||
    apiLoadingExpressMembershipVisibilityCondition ||
    apiLoadingExpressNonmemberLongTermHoldoutVisibilityCondition ||
    apiLoadingMatchesAnyRetailerIdVisibilityCondition ||
    apiLoadingMinimumPlatformVersionVisibilityCondition ||
    apiLoadingRetailerAvailabilityVisibilityCondition ||
    apiLoadingShoppedAtRetailerVisibilityCondition ||
    apiLoadingShoppedVisibilityCondition ||
    apiLoadingUserInteractionLimitVisibilityCondition ||
    apiLoadingWhitelistedPostalCodesVisibilityCondition ||
    apiLoadingWhitelistedZonesVisibilityCondition

  const submitError =
    apiErrorCountryIdsVisibilityCondition ||
    apiErrorDayOfWeekVisibilityCondition ||
    apiErrorExpressMembershipVisibilityCondition ||
    apiErrorExpressNonmemberLongTermHoldoutVisibilityCondition ||
    apiErrorMatchesAnyRetailerIdVisibilityCondition ||
    apiErrorMinimumPlatformVersionVisibilityCondition ||
    apiErrorRetailerAvailabilityVisibilityCondition ||
    apiErrorShoppedAtRetailerVisibilityCondition ||
    apiErrorShoppedVisibilityCondition ||
    apiErrorUserInteractionLimitVisibilityCondition ||
    apiErrorWhitelistedPostalCodesVisibilityCondition ||
    apiErrorWhitelistedZonesVisibilityCondition

  const onSubmit = (formData: VisibilityConditionFormData) => {
    const visibilityConditionType = formData.conditionType
    let submitPromise
    if (visibilityConditionType === VisibilityConditionTypeEnum.COUNTRY_IDS) {
      submitPromise = handleCountryIdsVisibilityConditionFormSubmit(placementId, formData)
    } else if (visibilityConditionType === VisibilityConditionTypeEnum.DAY_OF_WEEK) {
      submitPromise = handleDayOfWeekVisibilityConditionFormSubmit(placementId, formData)
    } else if (visibilityConditionType === VisibilityConditionTypeEnum.EXPRESS_MEMBERSHIP) {
      submitPromise = handleExpressMembershipVisibilityConditionFormSubmit(placementId, formData)
    } else if (
      visibilityConditionType === VisibilityConditionTypeEnum.EXPRESS_NONMEMBER_LONG_TERM_HOLDOUT
    ) {
      submitPromise = handleExpressNonmemberLongTermHoldoutVisibilityConditionFormSubmit(
        placementId,
        formData
      )
    } else if (visibilityConditionType === VisibilityConditionTypeEnum.MATCHES_ANY_RETAILER_ID) {
      submitPromise = handleMatchesAnyRetailerIdVisibilityConditionFormSubmit(placementId, formData)
    } else if (visibilityConditionType === VisibilityConditionTypeEnum.MINIMUM_PLATFORM_VERSION) {
      submitPromise = handleMinimumPlatformVersionVisibilityConditionFormSubmit(
        placementId,
        formData
      )
    } else if (visibilityConditionType === VisibilityConditionTypeEnum.RETAILER_AVAILABILITY) {
      submitPromise = handleRetailerAvailabilityVisibilityConditionFormSubmit(placementId, formData)
    } else if (visibilityConditionType === VisibilityConditionTypeEnum.SHOPPED_AT_RETAILER) {
      submitPromise = handleShoppedAtRetailerVisibilityConditionFormSubmit(placementId, formData)
    } else if (visibilityConditionType === VisibilityConditionTypeEnum.SHOPPED) {
      submitPromise = handleShoppedVisibilityConditionFormSubmit(placementId, formData)
    } else if (visibilityConditionType === VisibilityConditionTypeEnum.USER_INTERACTION_LIMIT) {
      submitPromise = handleUserInteractionLimitVisibilityConditionFormSubmit(placementId, formData)
    } else if (visibilityConditionType === VisibilityConditionTypeEnum.WHITELISTED_POSTAL_CODES) {
      submitPromise = handleWhitelistedPostalCodesVisibilityConditionFormSubmit(
        placementId,
        formData
      )
    } else if (visibilityConditionType === VisibilityConditionTypeEnum.WHITELISTED_ZONES) {
      submitPromise = handleWhitelistedZonesVisibilityConditionFormSubmit(placementId, formData)
    }

    submitPromise.then(response => {
      // only navigate on success
      if (response) {
        goToList()
      }
    })
  }

  const placementName =
    apiSuccessDetails && apiResultDetails.name ? apiResultDetails.name : placementId

  return (
    <div>
      <DocumentTitle titleId="bannersDomain.visibility-conditions.create.title" />
      <PageHeader
        addBottomPadding
        title={
          <BannerBreadcrumbs pageType="visibilityConditionCreate" placementName={placementName} />
        }
      />
      <div css={cardsContainer}>
        <VisibilityConditionForm
          onFormSubmit={onSubmit}
          submitError={submitError}
          isSubmitting={isSubmitting}
          onCancel={goToList}
        />
      </div>
    </div>
  )
}
